<template>
	<BaseDialog
		ref="termsOfUseDialog"
		:model-value="!userStore.hasUserAcceptedLatestTermsOfUse"
		content-class="terms-of-use-dialog"
		:close-button-visible="false"
		persistent
		:title="t('termsOfUseDialog.title')"
		data-testid="dialogTermsOfUseDialog"
	>
		<template #default>
			<div
				class="terms-of-use-dialog__text"
				data-testid="infoTermsOfUseText"
				v-html="formattedTermsOfUse"
			/>
		</template>
		<template #actions>
			<VBtnSecondary
				ref="buttonDeclineTermsOfUse"
				data-testid="buttonDeclineTermsOfUse"
				@click="declineTermsOfUse()"
			>
				{{ t('decline') }}
			</VBtnSecondary>
			<VBtnPrimary
				ref="buttonAcceptTermsOfUse"
				:loading="acceptTermsOfUse.isLoading"
				:disabled="acceptDisabled"
				data-testid="buttonAcceptTermsOfUse"
				@click="acceptTermsOfUse.execute()"
			>
				{{ t('accept') }}
			</VBtnPrimary>
		</template>
	</BaseDialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, ref, reactive, watch } from 'vue';
import type { ApiV1 } from '@teamworksdev/influencer/core';
import { isImpersonationLoginRoute } from '@/composables/useImpersonationAuth';
import { useInfluencer } from '@/composables/api/useInfluencer';
import { handleError } from '@/utils/errorHandling';
import { marked } from 'marked';
import { useUserStore } from '@/stores/userStore';
import { useAsyncState } from '@vueuse/core';
import { BaseDialog } from '@teamworksdev/vuetify';
import { useRouter, useRoute } from 'vue-router';

const { t } = useI18n();
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();
const inflcr = useInfluencer();

const termsOfUse = ref<ApiV1.TermsOfUse | undefined>(undefined);
const termsOfUseDialog = ref();

const getTermsOfUse = reactive(useAsyncState(
	async () => {
		try {
			const response = await inflcr.api.termsOfUse.getTermsOfUse();
			termsOfUse.value = response.data;
		} catch (error) {
			handleError(error);
		}
	},
	undefined,
	{ immediate: false },
));

const formattedTermsOfUse = computed(() => {
	return marked(termsOfUse.value?.terms ?? '');
});

function onSuccessfulAction() {
	termsOfUse.value = undefined;
}

const acceptDisabled = computed<boolean>(() => {
	return !termsOfUse.value || getTermsOfUse.isLoading || !termsOfUseDialog.value?.scrolledToBottom;
});

const acceptTermsOfUse = reactive(useAsyncState(
	async () => {
		try {
			await userStore.acceptTermsOfUse(termsOfUse.value!.version);

			if (isImpersonationLoginRoute(route)) {
				onSuccessfulAction();
				return;
			}

			const result = await userStore.setUserWithDefaultProfile();

			if (result.hasDefaultProfile) {
				router.push({ name: userStore.profileDefaultRoute });
			}

			onSuccessfulAction();
		} catch (error) {
			handleError(error);
		}
	}, undefined, { immediate: false },
));

function declineTermsOfUse() {
	window.location.assign('/logout');
}

watch(() => userStore.hasUserAcceptedLatestTermsOfUse, (newVal) => {
	if (!newVal) getTermsOfUse.execute();
}, { immediate: true });
</script>

<style lang="scss">
.terms-of-use-dialog__text {
    * {
        padding: revert;
        margin: revert;
    }
}
</style>
